/* eslint-disable no-console */
import { AnyAction, Reducer } from 'redux';
import { takeLatest, select, put } from 'redux-saga/effects';
import { INITIAL_RANDOM_NODE, FEED_TYPE } from '@constants';
import { IGeneral, IFeed, IAppearance } from '@interface/settings';
import { setPreferences } from '@lib/versum';
import { actions as globalActions } from '@state/global';

export interface ISettings {
  changelog: string;
  general: IGeneral;
  feed: IFeed;
  appearance: IAppearance;
}

export const initialState: ISettings = {
  changelog: '0.0.0',
  general: {
    currency: 'usd',
    network: process.env.LOGGEDOUT_TEZOS_NETWORK as string,
    node: INITIAL_RANDOM_NODE.value,
    changelog: true,
    crashreport: true,
  },
  feed: {
    type: FEED_TYPE.LATEST,
    nsfw: false,
    photosensitive: false,
  },
  appearance: {
    masonry: true,
    info: true,
    market: true,
    labels: true,
  },
};

export enum actions {
  SETTINGS_SAVE_PREFERENCE_FIRST_TIME_USER = 'SETTINGS_SAVE_PREFERENCE_FIRST_TIME_USER',
  SETTINGS_SET_PREFERENCES = 'SETTINGS_SET_PREFERENCES',
  // general
  SETTINGS_CHANGE_CURRENCY = 'SETTINGS_CHANGE_CURRENCY',
  SETTINGS_CHANGE_NETWORK = 'SETTINGS_CHANGE_NETWORK',
  SETTINGS_CHANGE_NODE = 'SETTINGS_CHANGE_NODE',
  SETTINGS_CHANGE_CHANGELOG = 'SETTINGS_CHANGE_CHANGELOG',
  SETTINGS_CHANGE_CRASHREPORT = 'SETTINGS_CHANGE_CRASHREPORT',
  // feed
  SETTINGS_CHANGE_FEED_TYPE = 'SETTINGS_CHANGE_FEED_TYPE',
  SETTINGS_CHANGE_FEED_NSFW = 'SETTINGS_CHANGE_FEED_NSFW',
  SETTINGS_CHANGE_FEED_PHOTOSENSITIVE = 'SETTINGS_CHANGE_FEED_PHOTOSENSITIVE',
  // misc
  SETTINGS_CHANGE_CHANGELOG_VERSION = 'SETTINGS_CHANGE_CHANGELOG_VERSION',
  // appearance
  SETTINGS_CHANGE_APPEARANCE_MASONRY = 'SETTINGS_CHANGE_APPEARANCE_MASONRY',
  SETTINGS_CHANGE_APPEARANCE_INFO = 'SETTINGS_CHANGE_APPEARANCE_INFO',
  SETTINGS_CHANGE_APPEARANCE_MARKET = 'SETTINGS_CHANGE_APPEARANCE_MARKET',
  SETTINGS_CHANGE_APPEARANCE_LABELS = 'SETTINGS_CHANGE_APPEARANCE_LABELS',
}

export const reducer: Reducer<ISettings, AnyAction> = (state = initialState, action) => {
  switch (action.type) {
    // changelog
    case actions.SETTINGS_CHANGE_CHANGELOG_VERSION:
      return { ...state, changelog: action.payload };
    // general
    case actions.SETTINGS_CHANGE_CURRENCY:
      return { ...state, general: { ...state.general, currency: action.payload } };
    case actions.SETTINGS_CHANGE_NETWORK:
      return { ...state, general: { ...state.general, network: action.payload } };
    case actions.SETTINGS_CHANGE_NODE:
      return { ...state, general: { ...state.general, node: action.payload } };
    case actions.SETTINGS_CHANGE_CHANGELOG:
      return { ...state, general: { ...state.general, changelog: action.payload } };
    case actions.SETTINGS_CHANGE_CRASHREPORT:
      return { ...state, general: { ...state.general, crashreport: action.payload } };
    // feed
    case actions.SETTINGS_CHANGE_FEED_TYPE:
      return { ...state, feed: { ...state.feed, type: action.payload } };
    case actions.SETTINGS_CHANGE_FEED_NSFW:
      return { ...state, feed: { ...state.feed, nsfw: action.payload } };
    case actions.SETTINGS_CHANGE_FEED_PHOTOSENSITIVE:
      return { ...state, feed: { ...state.feed, photosensitive: action.payload } };
    // appearance
    case actions.SETTINGS_SET_PREFERENCES:
      return { ...state, ...action.payload };
    case actions.SETTINGS_CHANGE_APPEARANCE_MASONRY:
      return { ...state, appearance: { ...state.appearance, masonry: action.payload } };
    case actions.SETTINGS_CHANGE_APPEARANCE_INFO:
      return { ...state, appearance: { ...state.appearance, info: action.payload } };
    case actions.SETTINGS_CHANGE_APPEARANCE_MARKET:
      return { ...state, appearance: { ...state.appearance, market: action.payload } };
    case actions.SETTINGS_CHANGE_APPEARANCE_LABELS:
      return { ...state, appearance: { ...state.appearance, labels: action.payload } };
    default:
      return state;
  }
};

function* updateUserPreferences() {
  const settings: ISettings = yield select((state) => state.settings);
  yield put({ type: globalActions.GLOBAL_GET_TEZOS, payload: settings.general.currency });
  yield setPreferences(settings);
}

export function* saga() {
  // first time users
  yield takeLatest(actions.SETTINGS_SAVE_PREFERENCE_FIRST_TIME_USER, updateUserPreferences);
  // misc
  yield takeLatest(actions.SETTINGS_CHANGE_CHANGELOG_VERSION, updateUserPreferences);
  // general
  yield takeLatest(actions.SETTINGS_CHANGE_CURRENCY, updateUserPreferences);
  yield takeLatest(actions.SETTINGS_CHANGE_NODE, updateUserPreferences);
  yield takeLatest(actions.SETTINGS_CHANGE_NETWORK, updateUserPreferences);
  yield takeLatest(actions.SETTINGS_CHANGE_CHANGELOG, updateUserPreferences);
  yield takeLatest(actions.SETTINGS_CHANGE_CRASHREPORT, updateUserPreferences);
  // feed
  yield takeLatest(actions.SETTINGS_CHANGE_FEED_TYPE, updateUserPreferences);
  yield takeLatest(actions.SETTINGS_CHANGE_FEED_NSFW, updateUserPreferences);
  yield takeLatest(actions.SETTINGS_CHANGE_FEED_PHOTOSENSITIVE, updateUserPreferences);
  // appearance
  yield takeLatest(actions.SETTINGS_CHANGE_APPEARANCE_MASONRY, updateUserPreferences);
  yield takeLatest(actions.SETTINGS_CHANGE_APPEARANCE_INFO, updateUserPreferences);
  yield takeLatest(actions.SETTINGS_CHANGE_APPEARANCE_MARKET, updateUserPreferences);
  yield takeLatest(actions.SETTINGS_CHANGE_APPEARANCE_LABELS, updateUserPreferences);
}
